/**
 * Admin User Management API Client
 *
 * Implements listUsers, createUser, updateUserRoles, deleteUser functions.
 * Handles authentication token injection.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4
 */

import type {
  ManagedUser,
  CreateUserRequest,
  UpdateUserRolesRequest,
  UpdateUserRequest,
  ApiResult,
  ApiError,
} from './types';

// API base URL - empty string for same-origin, or full URL for cross-origin
const API_BASE_URL = import.meta.env.VITE_API_MANAGEMENT_URL || '';
const API_BASE = `${API_BASE_URL}/api/admin`;

/**
 * Helper to make authenticated API requests
 */
async function authenticatedFetch<T>(
  token: string,
  url: string,
  options: RequestInit = {}
): Promise<ApiResult<T>> {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      let error: ApiError;
      try {
        error = await response.json();
      } catch {
        error = {
          message: `Request failed with status ${response.status}`,
          code: `HTTP_${response.status}`,
        };
      }
      return { success: false, error };
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { success: true, data: undefined as T };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (err) {
    const error: ApiError = {
      message: err instanceof Error ? err.message : 'Network error',
      code: 'NETWORK_ERROR',
    };
    return { success: false, error };
  }
}

/**
 * List all users in the system
 *
 * Requirements: 2.1
 *
 * @param token - Auth0 access token
 * @returns List of all managed users
 */
export async function listUsers(token: string): Promise<ApiResult<ManagedUser[]>> {
  return authenticatedFetch<ManagedUser[]>(token, `${API_BASE}/users`);
}

/**
 * Get a single user by ID
 *
 * @param token - Auth0 access token
 * @param userId - User ID to fetch
 * @returns The requested user
 */
export async function getUser(
  token: string,
  userId: string
): Promise<ApiResult<ManagedUser>> {
  return authenticatedFetch<ManagedUser>(token, `${API_BASE}/users/${userId}`);
}

/**
 * Create a new user with specified roles
 *
 * Requirements: 2.2
 *
 * @param token - Auth0 access token
 * @param userData - User data including email, name, and roles
 * @returns The created user
 */
export async function createUser(
  token: string,
  userData: CreateUserRequest
): Promise<ApiResult<ManagedUser>> {
  return authenticatedFetch<ManagedUser>(token, `${API_BASE}/users`, {
    method: 'POST',
    body: JSON.stringify(userData),
  });
}

/**
 * Update a user's roles
 *
 * Requirements: 2.3
 *
 * @param token - Auth0 access token
 * @param userId - User ID to update
 * @param roles - New roles to assign
 * @returns The updated user
 */
export async function updateUserRoles(
  token: string,
  userId: string,
  roles: UpdateUserRolesRequest
): Promise<ApiResult<ManagedUser>> {
  return authenticatedFetch<ManagedUser>(
    token,
    `${API_BASE}/users/${userId}/roles`,
    {
      method: 'PUT',
      body: JSON.stringify(roles),
    }
  );
}

/**
 * Update a user's profile information and/or roles
 *
 * Requirements: 2.3
 *
 * @param token - Auth0 access token
 * @param userId - User ID to update
 * @param userData - User data to update (name and/or roles)
 * @returns The updated user
 */
export async function updateUser(
  token: string,
  userId: string,
  userData: UpdateUserRequest
): Promise<ApiResult<ManagedUser>> {
  return authenticatedFetch<ManagedUser>(
    token,
    `${API_BASE}/users/${userId}`,
    {
      method: 'PUT',
      body: JSON.stringify(userData),
    }
  );
}

/**
 * Delete a user
 *
 * Requirements: 2.4
 *
 * @param token - Auth0 access token
 * @param userId - User ID to delete
 * @returns void on success
 */
export async function deleteUser(
  token: string,
  userId: string
): Promise<ApiResult<void>> {
  return authenticatedFetch<void>(token, `${API_BASE}/users/${userId}`, {
    method: 'DELETE',
  });
}
